import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { confirmJevFact, fetchJev, logJevEvent, readJevChat, type JevPayload } from '../api';

// JEV Assist strip (usability test 30 Sep → this layout):
//  header  : light · progress · persona · ⚑ Red flag (always) · collapse
//  ask     : opener line, ONE question at a time, answered facts as pills (tap to correct)
//  say     : at green, five short beats: open → evidence → their worry → offer box (arm, read exactly) → choose (+ Not now) → ask
//  doctor  : bridge line + "note a question for the doctor" (saved, shown back on the lead)
// If the API is off (404) or anything fails to load, the strip renders nothing; recording results is never blocked.

const LIGHT: Record<string, { dot: string; label: string }> = {
  red: { dot: 'bg-rose-500', label: 'Ask a few questions' },
  amber: { dot: 'bg-amber-400', label: 'Almost there' },
  green: { dot: 'bg-emerald-500', label: 'Ready' },
};
const PERSONA_LABEL: Record<string, string> = {
  C1: 'Asking early', C2: 'Trying a while', C3: 'Age 38+ · doctor first', C4: 'Repeated IUI', C5: 'After IVF',
  C6: 'Losses · doctor first', C7: 'Report worry · doctor first', C8: 'Second child',
};
const FACT_LABEL: Record<string, string> = {
  trying_duration: 'Trying', female_age_band: 'Her age', treatment_history: 'Treatment', losses: 'Losses',
  child_before: 'Child', husband_tested: 'Husband tested', main_worry: 'Worry',
};
const VALUE_LABEL: Record<string, string> = {
  lt_6m: '< 6 months', '6_12m': '6–12 months', '1_3y': '1–3 years', gt_3y: '3+ years',
  lt30: '< 30', '30_34': '30–34', '35_37': '35–37', '38_40': '38–40', gt40: '40+',
  none: 'None', medicines: 'Medicines', iui_1_2: 'IUI 1–2', iui_3plus: 'IUI 3+', ivf: 'IVF', one: 'One', two_plus: '2+',
  yes: 'Yes', no: 'No', unknown_to_patient: "Doesn't know",
  push_ivf: 'Fear of IVF push', cost: 'Cost', chances_guarantee: 'Wants certainty', past_hurt: 'Past hurt', family: 'Family',
  male_partner: 'Husband reluctant', distance: 'Distance', privacy: 'Privacy',
};
const WORRY_CHIPS = ['push_ivf', 'cost', 'chances_guarantee', 'past_hurt', 'family', 'male_partner', 'distance', 'privacy',
  'amh_low', 'tubal', 'pcos', 'miscarriage', 'unexplained', 'stress'];
const CHIP_LABEL: Record<string, string> = { ...VALUE_LABEL, amh_low: 'Low AMH', tubal: 'Tubes', pcos: 'PCOS', miscarriage: 'Miscarriage', unexplained: 'Unexplained', stress: 'Stress' };
const label = (v: string) => VALUE_LABEL[v] ?? v;
const Beat = ({ tag, children }: { tag: string; children: ReactNode }) => (
  <p><span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-violet-600">{tag}</span>{children}</p>
);

export default function JevStrip({ leadId, canEdit }: { leadId: string; canEdit: boolean }) {
  const [data, setData] = useState<JevPayload | null>(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [showWorries, setShowWorries] = useState(false);
  const [objection, setObjection] = useState<string | null>(null);
  const [offered, setOffered] = useState<string | null>(null);
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [flagged, setFlagged] = useState<string | null>(null);
  const [question, setQuestion] = useState('');

  const load = useCallback(async () => {
    try { setData(await fetchJev(leadId)); } catch { setOff(true); }
  }, [leadId]);
  useEffect(() => {
    setData(null); setOff(false); setObjection(null); setOffered(null); setEditing(null); setFlagged(null); setFlagsOpen(false); setQuestion('');
    void load();
  }, [load]);

  if (off || !data) return null;
  const s = data.strip;
  const light = LIGHT[s.light];

  const flash = (msg: string) => { setSaved(msg); setError(null); window.setTimeout(() => setSaved(null), 2000); };
  async function run(action: () => Promise<JevPayload | unknown>, ok: string, apply = true) {
    setBusy(true); setError(null);
    try {
      const res = await action();
      if (apply) setData(res as JevPayload);
      flash(ok);
      return true;
    } catch {
      setError("Couldn't save. Check the connection and tap again.");
      return false;
    } finally { setBusy(false); }
  }
  const answer = (key: string, value: string) => {
    if (!canEdit || busy) return;
    void run(() => confirmJevFact(leadId, key, value), 'Saved ✓').then((ok) => { if (ok) setEditing(null); });
  };
  const logEvent = (kind: 'offer' | 'objection' | 'red_flag' | 'doctor_question', value: string) =>
    run(() => logJevEvent(leadId, kind, value, data.assessment_id ?? null), 'Saved ✓', false);
  const offer = (n: string) => { if (!canEdit) return; setOffered(n); void logEvent('offer', n); };
  const pickObjection = (key: string) => {
    setObjection(key === objection ? null : key);
    if (key !== objection && canEdit) void logEvent('objection', key);
  };
  const raiseFlag = (key: string) => { setFlagged(key); if (canEdit) void logEvent('red_flag', key); };
  const saveQuestion = async () => {
    const text = question.trim();
    if (text.length < 3 || !canEdit) return;
    if (await logEvent('doctor_question', text)) { setQuestion(''); void load(); }
  };

  const next = s.ask[0];
  const editingFact = editing ? s.known?.find((k) => k.key === editing) : null;
  const card = objection ? data.cards[objection] : null;
  const flag = flagged ? data.red_flags?.find((f) => f.key === flagged) : null;
  const p = data.prompter;

  const chips = (key: string, options: string[], current: string | null) => (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {options.map((v) => (
        <button key={v} type="button" disabled={!canEdit || busy} onClick={() => answer(key, v)}
          className={`rounded-full border px-3 py-1 text-xs ${current === v ? 'border-violet-500 bg-violet-100 text-violet-900' : 'border-slate-200 bg-white text-slate-700'} disabled:opacity-50`}>
          {label(v)}
        </button>
      ))}
    </div>
  );

  return (
    <section aria-label="JEV guidance" className="rounded-2xl border border-violet-200 bg-violet-50/60 p-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open}>
          <span className={`h-2.5 w-2.5 flex-none rounded-full ${light.dot}`} />
          <span className="truncate text-xs font-semibold text-violet-900">
            JEV {s.completeness}/{s.total} · {s.persona ? PERSONA_LABEL[s.persona] : light.label}{s.worry && s.worry !== 'none' ? ` · worry: ${label(s.worry)}` : ''}
          </span>
        </button>
        <button type="button" onClick={() => setFlagsOpen((v) => !v)} aria-expanded={flagsOpen}
          className="flex-none rounded-full border border-rose-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700">⚑ Red flag</button>
      </div>
      {data.pending_signoff && <div className="mt-1 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Draft content · pending doctor sign-off</div>}

      {flagsOpen && (
        <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 p-2.5 text-sm" role="region" aria-label="Red flags">
          <p className="text-[11px] font-semibold text-rose-800">Stop the script. Tap what they said:</p>
          <div className="mt-1 grid gap-1.5">
            {(data.red_flags ?? []).map((f) => (
              <button key={f.key} type="button" onClick={() => raiseFlag(f.key)}
                className={`rounded-lg border px-2.5 py-1.5 text-left text-xs ${flagged === f.key ? 'border-rose-600 bg-rose-600 text-white' : 'border-rose-200 bg-white text-rose-900'}`}>
                {f.label}
              </button>
            ))}
          </div>
          {flag && (
            <div className="mt-2 rounded-lg bg-white p-2.5">
              <p className="text-[10px] font-semibold uppercase text-rose-700">Say now</p>
              <p className="text-sm font-medium text-slate-900">{flag.action}</p>
              <p className="mt-1 text-[11px] font-semibold text-rose-700">Then tell the duty doctor or your manager immediately.</p>
              <button type="button" onClick={() => { setFlagged(null); setFlagsOpen(false); }} className="mt-1.5 text-[11px] text-slate-500 underline">Clear: show the script again</button>
            </div>
          )}
        </div>
      )}

      {(saved || error) && (
        <div role="status" className={`mt-2 rounded-lg px-2 py-1 text-[11px] font-semibold ${error ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>{error ?? saved}</div>
      )}

      {open && (
        <div className="mt-2 space-y-3">
          {!canEdit && <p className="rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-900">Read-only: assign this lead to yourself to record answers.</p>}

          {!p && (
            <div className="space-y-2">
              <Beat tag="Start">{data.universal.opener}</Beat>
              {next && !editingFact && (
                <div className="rounded-xl bg-white p-2.5">
                  <div className="text-xs font-semibold text-slate-800">
                    Ask: {next.question}
                    {next.hint ? <span className="ml-1 font-normal text-violet-700">(maybe {label(next.hint)}, confirm)</span> : null}
                  </div>
                  {chips(next.key, next.options, next.hint)}
                  {s.ask.length > 1 && <div className="mt-1.5 text-[10px] text-slate-500">{s.ask.length - 1} more after this</div>}
                </div>
              )}
              {canEdit && data.extract_enabled && (
                <button type="button" onClick={() => void run(() => readJevChat(leadId), 'Hints added ✓')} disabled={busy}
                  className="text-[11px] font-semibold text-violet-700 underline disabled:opacity-50">{busy ? 'Reading…' : 'Read the chat for hints'}</button>
              )}
            </div>
          )}

          {editingFact && (
            <div className="rounded-xl bg-white p-2.5">
              <div className="text-xs font-semibold text-slate-800">Change: {editingFact.question}</div>
              {chips(editingFact.key, editingFact.options, editingFact.value)}
              <button type="button" onClick={() => setEditing(null)} className="mt-1.5 text-[11px] text-slate-500 underline">Cancel</button>
            </div>
          )}

          {(s.known?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1">
              {s.known!.map((k) => (
                <button key={k.key} type="button" disabled={!canEdit} onClick={() => setEditing(k.key)} title="Tap to change"
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${editing === k.key ? 'border-violet-500 bg-violet-100' : 'border-emerald-200 bg-emerald-50'} text-slate-700`}>
                  {FACT_LABEL[k.key] ?? k.key}: {label(k.value)} ✎
                </button>
              ))}
            </div>
          )}

          {p && !flagged && (
            <div className="space-y-2 rounded-xl bg-white p-2.5 text-sm text-slate-800">
              <Beat tag="Open">{p.opener}</Beat>
              <Beat tag="Explain">{p.evidence.text}</Beat>
              {p.worry && <Beat tag="Their worry">{p.worry.empathy} {p.worry.claim?.text ? `${p.worry.claim.text} ` : ''}{p.worry.next}</Beat>}
              {p.anti_push && <p className="font-medium">{p.anti_push}</p>}
              <div className="rounded-lg border-2 border-violet-300 bg-violet-50 p-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Offer {data.offer_arm} · read exactly</div>
                <p className="mt-0.5">{p.first_step}</p>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-600">They choose</div>
                <div className="mt-1 grid gap-1">
                  {p.options.map((o) => (
                    <button key={o.n} type="button" disabled={!canEdit} onClick={() => offer(String(o.n))}
                      className={`rounded-lg border px-2.5 py-1.5 text-left text-xs ${offered === String(o.n) ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'} disabled:opacity-60`}>
                      {o.n}. {o.line}
                    </button>
                  ))}
                  <button type="button" disabled={!canEdit} onClick={() => offer('none')}
                    className={`rounded-lg border px-2.5 py-1.5 text-left text-xs ${offered === 'none' ? 'border-slate-600 bg-slate-600 text-white' : 'border-dashed border-slate-300 bg-white text-slate-500'} disabled:opacity-60`}>
                    Not now
                  </button>
                </div>
              </div>
              <Beat tag="Ask">{p.ask}</Beat>
            </div>
          )}

          <div>
            <button type="button" onClick={() => setShowWorries((v) => !v)} aria-expanded={showWorries}
              className="text-[11px] font-semibold text-violet-800">{showWorries ? '▾' : '▸'} Patient worried about something?</button>
            {showWorries && (
              <div className="mt-1 flex flex-wrap gap-1">
                {WORRY_CHIPS.filter((k) => data.cards[k]).map((k) => (
                  <button key={k} type="button" onClick={() => pickObjection(k)}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${objection === k ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-700'}`}>
                    {CHIP_LABEL[k] ?? k}
                  </button>
                ))}
              </div>
            )}
            {card && (
              <div className="mt-1.5 rounded-xl bg-white p-2.5 text-sm text-slate-800">
                <p>{card.empathy}</p>
                {card.claim?.text && <p className="mt-1">{card.claim.text}</p>}
                <p className="mt-1 font-medium">{card.next}</p>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">If they ask about medicine or treatment</div>
            <p className="mt-0.5 text-xs text-slate-700">"{data.universal.medical ?? data.universal.dont_know}"</p>
            {canEdit && (
              <div className="mt-1.5 flex gap-1.5">
                <label htmlFor={`jev-dq-${leadId}`} className="sr-only">Question for the doctor</label>
                <input id={`jev-dq-${leadId}`} value={question} maxLength={300} onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Note a question for the doctor" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs" />
                <button type="button" onClick={() => void saveQuestion()} disabled={busy || question.trim().length < 3}
                  className="flex-none rounded-lg bg-slate-800 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">Save</button>
              </div>
            )}
            {(data.doctor_questions?.length ?? 0) > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-[11px] text-slate-600">
                {data.doctor_questions!.map((q, i) => <li key={`${q.at}-${i}`}>• {q.text}</li>)}
              </ul>
            )}
          </div>
          <p className="text-[10px] text-slate-500">Educate on the process; never name medicines, doses or chances. Never promise a baby.</p>
        </div>
      )}
    </section>
  );
}
